import React, { useState, useEffect, useMemo } from 'react';
import { Save, FolderOpen, Trash2, Edit2, Search, X, Check, RefreshCw, PlusCircle, AlertCircle } from 'lucide-react';
import { Group, GroupSelection } from '../types';
import { ConfirmModal } from './ConfirmModal';
import { getGroupSelections, createGroupSelection, updateGroupSelection, deleteGroupSelection } from '../services/api';

interface GroupSelectionManagerProps {
  groups: Group[];
  selectedGroups: Set<string>;
  onSelectionLoad: (groupIds: string[]) => void;
  onSelectionChange: (groupIds: string[]) => void;
  toast?: {
    success: (message: string) => void;
    error: (message: string) => void;
    warning: (message: string) => void;
    info: (message: string) => void;
  };
}

export const GroupSelectionManager: React.FC<GroupSelectionManagerProps> = ({
  groups,
  selectedGroups,
  onSelectionLoad,
  onSelectionChange,
  toast
}) => {
  const [savedSelections, setSavedSelections] = useState<GroupSelection[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectionToDelete, setSelectionToDelete] = useState<string | null>(null);

  // Search query for filtering saved selections
  const [searchQuery, setSearchQuery] = useState('');

  // Save Modal States
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveMode, setSaveMode] = useState<'create' | 'overwrite'>('create');
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit Modal States (for editing directly from list)
  const [editingSelection, setEditingSelection] = useState<GroupSelection | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editUpdateGroups, setEditUpdateGroups] = useState(false);

  // Track if selections have been loaded
  const [selectionsLoaded, setSelectionsLoaded] = useState(false);

  // Load saved selections from API when groups are available
  useEffect(() => {
    if (groups.length > 0 && !selectionsLoaded) {
      loadSelections();
      setSelectionsLoaded(true);
    }
  }, [groups.length, selectionsLoaded]);

  // Reload selections when groups change (to update counts)
  useEffect(() => {
    if (savedSelections.length > 0 && groups.length > 0 && selectionsLoaded) {
      const updatedSelections = savedSelections.map(selection => {
        const validGroupIds = selection.groupIds.filter(groupId =>
          groups.some(g => g.id === groupId)
        );
        return {
          ...selection,
          groupIds: validGroupIds
        };
      }).filter(selection => selection.groupIds.length > 0);

      const hasChanges = updatedSelections.length !== savedSelections.length ||
        updatedSelections.some((sel, idx) =>
          sel.groupIds.length !== savedSelections[idx]?.groupIds.length
        );

      if (hasChanges) {
        setSavedSelections(updatedSelections);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.length, selectionsLoaded]);

  const loadSelections = async () => {
    try {
      const response = await getGroupSelections();
      if (response.success) {
        const validSelections = response.selections.map(selection => {
          const validGroupIds = selection.groupIds.filter(groupId =>
            groups.some(g => g.id === groupId)
          );
          return {
            ...selection,
            groupIds: validGroupIds
          };
        }).filter(selection => selection.groupIds.length > 0);

        setSavedSelections(validSelections);
      }
    } catch (error: any) {
      console.error('Error loading group selections:', error);
      toast?.error('Error al cargar selecciones guardadas');
    }
  };

  // Open Save Modal
  const handleOpenSaveModal = () => {
    if (selectedGroups.size === 0) {
      toast?.error('No hay grupos seleccionados para guardar');
      return;
    }
    setSaveName('');
    setSaveDescription('');
    setSelectedTargetId(savedSelections.length > 0 ? savedSelections[0].id : '');
    setModalSearchQuery('');
    setSaveMode('create');
    setShowSaveModal(true);
  };

  // Handle Target selection in overwrite mode
  const handleSelectTarget = (targetId: string) => {
    setSelectedTargetId(targetId);
    const target = savedSelections.find(s => s.id === targetId);
    if (target) {
      setSaveName(target.name);
      setSaveDescription(target.description || '');
    }
  };

  // Save selection (Create new or Overwrite)
  const handleSaveSelection = async () => {
    if (!saveName.trim()) {
      toast?.error('Por favor ingresa un nombre para la selección');
      return;
    }
    if (selectedGroups.size === 0) {
      toast?.error('No hay grupos seleccionados para guardar');
      return;
    }

    setIsSubmitting(true);
    try {
      if (saveMode === 'create') {
        const response = await createGroupSelection(
          saveName.trim(),
          saveDescription.trim(),
          Array.from(selectedGroups)
        );

        if (response.success) {
          toast?.success(`Selección "${saveName}" creada exitosamente`);
          setShowSaveModal(false);
          await loadSelections();
        }
      } else {
        if (!selectedTargetId) {
          toast?.error('Por favor selecciona la lista que deseas sobrescribir');
          setIsSubmitting(false);
          return;
        }

        const response = await updateGroupSelection(
          selectedTargetId,
          saveName.trim(),
          saveDescription.trim(),
          Array.from(selectedGroups)
        );

        if (response.success) {
          toast?.success(`Selección "${saveName}" actualizada exitosamente`);
          setShowSaveModal(false);
          await loadSelections();
        }
      }
    } catch (error: any) {
      console.error('Error saving group selection:', error);
      toast?.error(error.response?.data?.error || error.message || 'Error al guardar la selección');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open Edit Modal for a specific selection
  const handleOpenEditModal = (selection: GroupSelection) => {
    setEditingSelection(selection);
    setEditName(selection.name);
    setEditDescription(selection.description || '');
    setEditUpdateGroups(false);
  };

  // Save changes from Edit Modal
  const handleSaveEdit = async () => {
    if (!editingSelection) return;
    if (!editName.trim()) {
      toast?.error('El nombre no puede estar vacío');
      return;
    }

    setIsSubmitting(true);
    try {
      const groupIds = editUpdateGroups ? Array.from(selectedGroups) : editingSelection.groupIds;
      if (groupIds.length === 0) {
        toast?.error('La selección no puede quedarse sin grupos');
        setIsSubmitting(false);
        return;
      }

      const response = await updateGroupSelection(
        editingSelection.id,
        editName.trim(),
        editDescription.trim(),
        groupIds
      );

      if (response.success) {
        toast?.success(`Selección "${editName}" actualizada`);
        setEditingSelection(null);
        await loadSelections();
      }
    } catch (error: any) {
      console.error('Error updating selection:', error);
      toast?.error(error.response?.data?.error || error.message || 'Error al actualizar la selección');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load selection
  const loadSelection = (selection: GroupSelection) => {
    onSelectionLoad(selection.groupIds);
    if (toast) {
      toast.success(`Selección "${selection.name}" cargada`);
    }
  };

  // Delete selection
  const deleteSelection = (id: string) => {
    setSelectionToDelete(id);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    if (selectionToDelete) {
      try {
        const response = await deleteGroupSelection(selectionToDelete);
        if (response.success) {
          toast?.success('Selección eliminada exitosamente');
          await loadSelections();
        }
      } catch (error: any) {
        console.error('Error deleting group selection:', error);
        toast?.error(error.response?.data?.error || 'Error al eliminar la selección');
      }
    }
    setShowConfirmModal(false);
    setSelectionToDelete(null);
  };

  // Get selection info
  const getSelectionInfo = (groupIds: string[]) => {
    const selected = groups.filter(g => groupIds.includes(g.id));
    const totalParticipants = selected.reduce((sum, g) => sum + (g.participants || 0), 0);
    return {
      count: selected.length,
      participants: totalParticipants,
      names: selected.map(g => g.name).slice(0, 3),
      missingCount: groupIds.length - selected.length
    };
  };

  const currentSelectionInfo = getSelectionInfo(Array.from(selectedGroups));

  // Filter saved selections based on search query
  const filteredSavedSelections = useMemo(() => {
    if (!searchQuery.trim()) return savedSelections;
    const q = searchQuery.toLowerCase().trim();
    return savedSelections.filter(
      s => s.name.toLowerCase().includes(q) || (s.description && s.description.toLowerCase().includes(q))
    );
  }, [savedSelections, searchQuery]);

  // Filter selections for modal overwrite
  const filteredModalSelections = useMemo(() => {
    if (!modalSearchQuery.trim()) return savedSelections;
    const q = modalSearchQuery.toLowerCase().trim();
    return savedSelections.filter(
      s => s.name.toLowerCase().includes(q) || (s.description && s.description.toLowerCase().includes(q))
    );
  }, [savedSelections, modalSearchQuery]);

  return (
    <div className="space-y-4">
      {/* Current Selection Info */}
      <div className="bg-theme-card p-4 rounded-xl shadow-sm border border-theme">
        <div className="flex justify-between items-center mb-3">
          <h4 className="font-semibold text-theme-main">Selección Actual</h4>
          <div className="flex gap-2">
            <button
              onClick={handleOpenSaveModal}
              disabled={selectedGroups.size === 0}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedGroups.size === 0
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
              }`}
            >
              <Save size={14} />
              Guardar
            </button>
          </div>
        </div>

        <div className="text-sm text-theme-muted">
          <span className="font-bold text-theme-main">{currentSelectionInfo.count}</span> grupos seleccionados
          <span className="ml-4">
            <span className="font-bold text-theme-main">{currentSelectionInfo.participants.toLocaleString()}</span> participantes totales
          </span>
        </div>

        {currentSelectionInfo.names.length > 0 && (
          <div className="mt-2 text-xs text-theme-muted">
            Grupos: {currentSelectionInfo.names.join(', ')}
            {currentSelectionInfo.count > 3 && ` y ${currentSelectionInfo.count - 3} más`}
          </div>
        )}
      </div>

      {/* Saved Selections */}
      <div className="bg-theme-card p-4 rounded-xl shadow-sm border border-theme">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-theme-main flex items-center gap-2">
            <FolderOpen size={16} />
            Selecciones Guardadas ({savedSelections.length})
          </h4>
        </div>

        {/* Search input if there are saved selections */}
        {savedSelections.length > 0 && (
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar selecciones guardadas..."
              className="w-full pl-8 pr-8 py-1.5 text-xs bg-theme-base border border-theme rounded-lg focus:outline-none focus:border-primary-500 text-theme-main placeholder-theme-muted"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-theme-muted hover:text-theme-main"
              >
                <X size={13} />
              </button>
            )}
          </div>
        )}

        {savedSelections.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <FolderOpen size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No hay selecciones guardadas</p>
          </div>
        ) : filteredSavedSelections.length === 0 ? (
          <div className="text-center py-6 text-slate-400">
            <Search size={24} className="mx-auto mb-1 opacity-50" />
            <p className="text-xs">No se encontraron selecciones con "{searchQuery}"</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {filteredSavedSelections.map(selection => {
              const info = getSelectionInfo(selection.groupIds);
              return (
                <div
                  key={selection.id}
                  className="flex items-center justify-between p-3 bg-theme-base rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors border border-transparent hover:border-theme"
                >
                  <div className="flex-1 min-w-0 pr-2">
                    <h5 className="font-medium text-theme-main text-sm truncate">{selection.name}</h5>
                    {selection.description && (
                      <p className="text-xs text-theme-muted truncate mb-1">{selection.description}</p>
                    )}
                    <div className="text-xs text-theme-muted flex items-center flex-wrap gap-x-2">
                      <span>{info.count} grupos</span>
                      <span>•</span>
                      <span>{info.participants.toLocaleString()} participantes</span>
                      {info.missingCount > 0 && (
                        <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                          ({info.missingCount} no disponible{info.missingCount > 1 ? 's' : ''})
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      Guardado: {selection.createdAt instanceof Date
                        ? selection.createdAt.toLocaleDateString()
                        : new Date(selection.createdAt).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => loadSelection(selection)}
                      className="p-1.5 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-950/40 rounded transition-colors"
                      title="Cargar selección"
                    >
                      <FolderOpen size={15} />
                    </button>
                    <button
                      onClick={() => handleOpenEditModal(selection)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded transition-colors"
                      title="Editar selección"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => deleteSelection(selection.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded transition-colors"
                      title="Eliminar selección"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Save Modal (Create New or Overwrite Existing) */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-theme-card p-6 rounded-2xl shadow-2xl max-w-lg w-full border border-theme animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-theme-main flex items-center gap-2">
                <Save className="text-primary-600" size={20} />
                Guardar Selección de Grupos
              </h3>
              <button
                onClick={() => setShowSaveModal(false)}
                className="text-theme-muted hover:text-theme-main p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            {/* Mode Selector Tabs (Create vs Overwrite) */}
            {savedSelections.length > 0 && (
              <div className="grid grid-cols-2 gap-2 bg-theme-base p-1 rounded-xl mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setSaveMode('create');
                    setSaveName('');
                    setSaveDescription('');
                  }}
                  className={`flex items-center justify-center gap-2 py-2 text-xs font-semibold rounded-lg transition-all ${
                    saveMode === 'create'
                      ? 'bg-theme-card text-primary-600 shadow-sm'
                      : 'text-theme-muted hover:text-theme-main'
                  }`}
                >
                  <PlusCircle size={14} />
                  Guardar como Nueva
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSaveMode('overwrite');
                    if (savedSelections.length > 0) {
                      handleSelectTarget(savedSelections[0].id);
                    }
                  }}
                  className={`flex items-center justify-center gap-2 py-2 text-xs font-semibold rounded-lg transition-all ${
                    saveMode === 'overwrite'
                      ? 'bg-theme-card text-primary-600 shadow-sm'
                      : 'text-theme-muted hover:text-theme-main'
                  }`}
                >
                  <RefreshCw size={14} />
                  Sobrescribir Existente
                </button>
              </div>
            )}

            <div className="space-y-4">
              {/* Overwrite mode target selection */}
              {saveMode === 'overwrite' && (
                <div>
                  <label className="block text-xs font-semibold text-theme-main mb-1">
                    Selecciona la lista a sobrescribir *
                  </label>
                  {savedSelections.length > 4 && (
                    <div className="relative mb-2">
                      <Search size={13} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-theme-muted" />
                      <input
                        type="text"
                        value={modalSearchQuery}
                        onChange={(e) => setModalSearchQuery(e.target.value)}
                        placeholder="Filtrar listas..."
                        className="w-full pl-7 pr-3 py-1.5 text-xs bg-theme-base border border-theme rounded-lg text-theme-main"
                      />
                    </div>
                  )}
                  <div className="max-h-40 overflow-y-auto space-y-1.5 p-1 border border-theme rounded-lg bg-theme-base">
                    {filteredModalSelections.map(sel => (
                      <div
                        key={sel.id}
                        onClick={() => handleSelectTarget(sel.id)}
                        className={`p-2 rounded-lg cursor-pointer text-xs flex items-center justify-between transition-colors ${
                          selectedTargetId === sel.id
                            ? 'bg-primary-600 text-white font-medium shadow-sm'
                            : 'hover:bg-theme-card text-theme-main'
                        }`}
                      >
                        <div className="truncate mr-2">
                          <span className="font-semibold">{sel.name}</span>
                          {sel.description && (
                            <span className={`block text-[11px] truncate ${selectedTargetId === sel.id ? 'text-primary-100' : 'text-theme-muted'}`}>
                              {sel.description}
                            </span>
                          )}
                        </div>
                        <span className={`text-[11px] shrink-0 ${selectedTargetId === sel.id ? 'text-primary-100' : 'text-theme-muted'}`}>
                          {sel.groupIds.length} grupos
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-theme-main mb-1">
                  Nombre de la selección *
                </label>
                <input
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="Ej: Clientes VIP, Grupos de marketing..."
                  className="w-full px-3 py-2 text-sm bg-theme-base border border-theme rounded-lg focus:outline-none focus:border-primary-500 text-theme-main"
                  maxLength={50}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-theme-main mb-1">
                  Descripción (opcional)
                </label>
                <textarea
                  value={saveDescription}
                  onChange={(e) => setSaveDescription(e.target.value)}
                  placeholder="Describe el propósito o detalles de esta selección..."
                  className="w-full px-3 py-2 text-sm bg-theme-base border border-theme rounded-lg resize-none h-18 focus:outline-none focus:border-primary-500 text-theme-main"
                  maxLength={200}
                />
              </div>

              {/* Status summary */}
              <div className={`p-3 rounded-xl text-xs flex items-start gap-2 ${
                saveMode === 'overwrite'
                  ? 'bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300'
                  : 'bg-primary-500/10 border border-primary-500/30 text-primary-700 dark:text-primary-300'
              }`}>
                {saveMode === 'overwrite' ? (
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                ) : (
                  <Check size={16} className="shrink-0 mt-0.5" />
                )}
                <div>
                  {saveMode === 'overwrite' ? (
                    <span>
                      Se actualizará la selección con los <b>{currentSelectionInfo.count}</b> grupos seleccionados actualmente ({currentSelectionInfo.participants.toLocaleString()} participantes).
                    </span>
                  ) : (
                    <span>
                      Se guardará una nueva selección con <b>{currentSelectionInfo.count}</b> grupos seleccionados ({currentSelectionInfo.participants.toLocaleString()} participantes).
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowSaveModal(false)}
                className="px-4 py-2 text-sm text-theme-muted hover:text-theme-main transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveSelection}
                disabled={!saveName.trim() || isSubmitting || (saveMode === 'overwrite' && !selectedTargetId)}
                className="px-5 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
              >
                {isSubmitting ? 'Guardando...' : saveMode === 'overwrite' ? 'Sobrescribir' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Direct Modal */}
      {editingSelection && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-theme-card p-6 rounded-2xl shadow-2xl max-w-md w-full border border-theme animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-theme-main flex items-center gap-2">
                <Edit2 className="text-blue-600" size={18} />
                Editar Selección
              </h3>
              <button
                onClick={() => setEditingSelection(null)}
                className="text-theme-muted hover:text-theme-main p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-theme-main mb-1">
                  Nombre de la selección *
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-theme-base border border-theme rounded-lg focus:outline-none focus:border-primary-500 text-theme-main"
                  maxLength={50}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-theme-main mb-1">
                  Descripción (opcional)
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-theme-base border border-theme rounded-lg resize-none h-18 focus:outline-none focus:border-primary-500 text-theme-main"
                  maxLength={200}
                />
              </div>

              {/* Option to replace groups with current selection */}
              <div
                onClick={() => setEditUpdateGroups(!editUpdateGroups)}
                className={`p-3 rounded-xl border cursor-pointer transition-all ${
                  editUpdateGroups
                    ? 'bg-blue-500/10 border-blue-500/40 text-blue-800 dark:text-blue-200'
                    : 'bg-theme-base border-theme text-theme-muted hover:text-theme-main'
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={editUpdateGroups}
                    onChange={(e) => setEditUpdateGroups(e.target.checked)}
                    className="mt-0.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <p className="text-xs font-semibold text-theme-main">
                      Reemplazar grupos con la selección actual
                    </p>
                    <p className="text-[11px] mt-0.5">
                      {editUpdateGroups ? (
                        <span className="font-medium">
                          Se actualizará a los <b>{currentSelectionInfo.count}</b> grupos seleccionados actualmente.
                        </span>
                      ) : (
                        <span>
                          Mantendrá sus <b>{editingSelection.groupIds.length}</b> grupos originales.
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setEditingSelection(null)}
                className="px-4 py-2 text-sm text-theme-muted hover:text-theme-main transition-colors"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={!editName.trim() || isSubmitting}
                className="px-5 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
              >
                {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmDelete}
        title="Eliminar Selección"
        message="¿Estás seguro de que deseas eliminar esta selección guardada? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};
